import React, { useRef, useImperativeHandle, forwardRef, useState, useEffect } from "react";
import { useSetState } from "utils/functions.utils";
import "./dropdown.ui.scss";
import Assets from "../../../imports/assets.import";
import Select, { OptionProps, components as SelectComponents } from "react-select";
import CreatableSelect from "react-select/creatable";
import { IDropdownData } from "utils/interface.utils";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import _ from "lodash";
import { Colors, FontFamily, Models } from "utils/imports.utils";

interface IDropdownProps extends StateManagerProps {
  color?:string,
  data: Array<IDropdownData>;
  className?: string;
  value: any;
  clear?: boolean;
  containerClassName?: string;
  error?: any;
  icon?: string;
  label?: string;
  id?: string;
  iconHeight?: string;
  iconWidth?: string;
  onChange?: (selectedOption: any) => void;
  // When true, renders as a single editable combobox: the same field the user picks from the
  // dropdown list with can also be typed into directly to enter a value not in `data`.
  isCreatable?: boolean;
  isValidNewOption?: (inputValue: string) => boolean;
  formatCreateLabel?: (inputValue: string) => string;
}

// react-select hides the real <input> (opacity: 0) right after a value is selected, expecting the
// field to blur/refocus before being edited again -- it assumes SingleValue will show the text
// instead. Since we keep `inputValue` populated so the field stays editable without a blur/refocus
// round-trip, SingleValue is suppressed too (react-select never renders it while inputValue is
// non-empty), so both would be invisible at once. Forcing isHidden={false} keeps the input visible.
// Defined at module scope (not inside Dropdown) so it has a stable identity across renders --
// otherwise React would unmount/remount the real <input> DOM node on every keystroke, dropping
// focus and breaking typing/Enter-to-commit mid-interaction.
const AlwaysVisibleInput = (inputProps: any) => <SelectComponents.Input {...inputProps} isHidden={false} />;

export type Ref = any;
const Dropdown = forwardRef<Ref, IDropdownProps>((props, ref) => {
  // State
  const [state, setState] = useSetState({
    isOpen: false,
    active: "",
    // selectedOption:props.value,
  });

  const dropdownRef: any = useRef();

  // Creatable mode's typed input is a separate text buffer from the selected value -- react-select
  // doesn't seed it with the current selection's label, so focusing the field to edit an existing
  // value (e.g. change "54" to "58") starts from an empty string instead of showing "54" to edit.
  // Keeping this synced to props.value (rather than only seeding on focus) also covers editing
  // again immediately after a commit, without needing to blur/refocus first.
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (props.isCreatable) {
      setInputValue(props.value?.label ?? "");
    }
  }, [props.isCreatable, props.value?.label]);

  //Logic
  const customStyles: any = {
    multiValueRemove: (provided, state) => ({
      ...provided,
      "&:hover": {
        backgroundColor: "transparent",
        color: "white",
      },
    }),
    // The typed input otherwise falls back to the browser default font, which doesn't match the
    // option list's font (set via CustomOption below) -- keep them visually consistent.
    ...(props.isCreatable ? {
      input: (provided: any) => ({ ...provided, fontFamily: FontFamily.regular, fontSize: "12px" }),
      singleValue: (provided: any) => ({ ...provided, fontFamily: FontFamily.regular, fontSize: "12px" }),
      // The properties panel's scroll container (`.properties_parameters_container`) clips this
      // dropdown's menu since it's rendered inline inside it -- portal it to <body> instead so it
      // isn't cut off by that ancestor's overflow, matching how the panel itself scrolls.
      menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
    } : {}),
};

const ClearIndicator = props => {
    return (
      <div style={{ paddingRight: "10px", paddingTop: "5px" }} onClick={props.clearValue}>
        <img src={Assets.close_black} height={20} width={20} />
      </div>
    );
};

const gettingDropdownData = () => {
    return props.data?.map((item,index) => {
      if (item.hasOwnProperty('label') && item.hasOwnProperty('value')) {
        return item;
      } else if (item.hasOwnProperty('label') && !item.hasOwnProperty('value')) {
        return {
            //@ts-ignore
            value: item.label,
            ...item
        };
      } else {
        return item;
      }
    });
};

const DropdownIndicator = dropdownProps => {
    return (
      <div>
        <img src={getDropDownIndicatorIcon()} height={props.iconHeight || 16} width={props.iconWidth || 16} />
      </div>
    );
};


const getDropDownIndicatorIcon = () => {
    return Assets.caret_down
}

const CustomOption = (props: OptionProps<any>) => {
    const { children, innerRef, innerProps, isDisabled, isSelected, isFocused, data } = props;
    return (
      <div
        ref={innerRef}
        {...innerProps}
        className={"drop_down_data_container"}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent:"flex-start",
          backgroundColor: isDisabled
            ? undefined
            : isSelected
            ?  "#F7F7F8"
            : isFocused
            ? '#F7F7F8'
            : "#fff",
          color: isDisabled ? '#ccc' : '#000',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          borderRadius: '8px',
          width: "90%",
          margin: "0 auto"
        }}
      >
        {/* <span
          style={{
            height: 6,
            width: 6,
            borderRadius: '50%',
            backgroundColor:colorStyles[data.value],
            display: 'inline-block',
            marginRight: 10,
          }}
        /> */}
        <span style={{color: isSelected ? Colors.dropDownItemColorSelected : Colors.dropDownItemColor,fontSize:"12px", fontFamily: isSelected ? FontFamily.medium : FontFamily.regular}} className={"drop_down_data_item"}>{children}</span>
      </div>
    );
  };
    
  const SelectComponent: any = props.isCreatable ? CreatableSelect : Select;

  return (
    <div className={`dropdown_container ${props.containerClassName || ""}`}>
            {props.label && <div className="dropdown_label_wrapper">{props.label}</div>}
            <SelectComponent
                value={props.value}
                onChange={(selectedOption: any) => {
                    props.onChange?.(selectedOption);
                }}
                ref={dropdownRef}
                maxMenuHeight={200}
                styles={customStyles}
                options={gettingDropdownData()}
                menuPlacement="auto"
                placeholder={props.placeholder}
                isSearchable={!!props.isCreatable}
                isValidNewOption={props.isValidNewOption}
                formatCreateLabel={props.formatCreateLabel}
                {...(props.isCreatable ? {
                    menuPortalTarget: document.body,
                    menuPosition: "fixed",
                    inputValue,
                    onInputChange: (newValue: string, actionMeta: any) => {
                        if (actionMeta.action === "input-change") setInputValue(newValue);
                        return newValue;
                    },
                    // If the user types then clicks/tabs away without committing a new value,
                    // revert the visible text back to the current selection instead of leaving
                    // the abandoned, unconfirmed text in the field.
                    onBlur: () => setInputValue(props.value?.label ?? ""),
                } : {})}
                components={{
                    DropdownIndicator,
                    ClearIndicator,
                    IndicatorSeparator: () => {
                      return null;
                    },
                    Option:CustomOption,
                    ...(props.isCreatable ? { Input: AlwaysVisibleInput } : {}),
                }}
                classNames={{
                    // control: (state) => (props.className ? props.className : state.isFocused ? "dropdown_control_active" : "dropdown_control"),
                    control: state => (state.isFocused ? "dropdown_control_active" : "dropdown_control"),
                    indicatorSeparator: state => (state.isFocused ? "dropdown_indicator_separator" : "dropdown_indicator_separator"),
                    multiValue: state => (state.isFocused ? "dropdown_multi_value" : "dropdown_multi_value"),
                    multiValueLabel: state => (state.isFocused ? "dropdown_multi_value_label" : "dropdown_multi_value_label"),
                    // singleValue: state => (
                    //   getClassName() === "drop_down_under_review" ? "drop_down_under_review_single" : 
                    //   getClassName() === "drop_down_resolved" ? "drop_down_resolved_single" :
                    //   getClassName() === "drop_down_closed" ?  "drop_down_closed_single" : ""
                    // ),
                    valueContainer: state => (state.isDisabled ? "dropdown_input_value" : "dropdown_input_value"),
                    option: state => (state.isSelected ? "menu_list_item_selected" : state.isFocused ? "menu_list_item_focused" : "menu_list_item"),
                    input: state => "select_box_input",
                  }}
            />
            {props.error && props.error.length > 0 && <div className="input_field_error">{props.error}</div>}
        </div>
  );
})

export default Dropdown;
